/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { pgTable, text, serial, integer, timestamp } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Content type categorization based on file formats
export const CONTENT_TYPES = {
  IMAGE: 'image',
  DOCUMENT: 'document',
  PRESENTATION: 'presentation',
  SPREADSHEET: 'spreadsheet',
  VIDEO: 'video',
  AUDIO: 'audio',
  DATA: 'data',
  WEB: 'web',
  CODE: 'code',
  MATERIAL: 'material' // fallback for uncategorized content
} as const;

export type ContentType = typeof CONTENT_TYPES[keyof typeof CONTENT_TYPES];

export const contents = pgTable("contents", {
  id: serial("id").primaryKey(),
  title: text("title").notNull(),
  description: text("description"),
  contentType: text("content_type").notNull(), // Categorization of content (image, document, video, etc.)
  content: text("content").notNull(),
  topicIds: integer("topic_ids").array(),
  skillIds: integer("skill_ids").array(),
  thumbnailUrl: text("thumbnail_url"),
  resourceUrl: text("resource_url"), // URL to the external resource (video, PDF, image, etc.)
  summary: text("summary"), // Summary of the content generated by LLM
  mimeType: text("mime_type"), // MIME type of the content (e.g., application/pdf, image/jpeg)
  size: integer("size"), // File size in bytes
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});


// Content libraries table for organizing content
export const contentLibraries = pgTable("content_libraries", {
  id: serial("id").primaryKey(),
  title: text("title").notNull(),
  description: text("description"),
  thumbnailUrl: text("thumbnail_url"),
  contentIds: integer("content_ids").array(), // Array of content IDs in this library
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Insert schemas
export const insertContentSchema = createInsertSchema(contents)
  .omit({ id: true, createdAt: true, updatedAt: true })
  .extend({
    contentType: z.enum([
      CONTENT_TYPES.IMAGE,
      CONTENT_TYPES.DOCUMENT,
      CONTENT_TYPES.PRESENTATION,
      CONTENT_TYPES.SPREADSHEET,
      CONTENT_TYPES.VIDEO,
      CONTENT_TYPES.AUDIO,
      CONTENT_TYPES.DATA,
      CONTENT_TYPES.WEB,
      CONTENT_TYPES.CODE,
      CONTENT_TYPES.MATERIAL
    ])
  });
export const insertContentLibrarySchema = createInsertSchema(contentLibraries).omit({ id: true, createdAt: true, updatedAt: true });

// Types
export type Content = typeof contents.$inferSelect;
export type InsertContent = z.infer<typeof insertContentSchema>;
export type ContentLibrary = typeof contentLibraries.$inferSelect;
export type InsertContentLibrary = z.infer<typeof insertContentLibrarySchema>; 